/* eslint-disable consistent-return */
import React from 'react'
import { Element } from 'domhandler'
import { Editor, Element as SlateElement, Range, Transforms } from 'slate'
import { isNil } from 'lodash'
import { v4 } from 'uuid'
import { Button } from '@/components/buttons/Button'

export const CustomEditor = {
  isBoldMarkActive(editor) {
    const marks = Editor.marks(editor)
    return marks ? marks.bold === true : false
  },

  isItalicMarkActive(editor) {
    const marks = Editor.marks(editor)
    return marks ? marks.italic === true : false
  },

  toggleBoldMark(editor) {
    const isActive = CustomEditor.isBoldMarkActive(editor)
    if (isActive) {
      Editor.removeMark(editor, 'bold')
    } else {
      Editor.addMark(editor, 'bold', true)
    }
  },

  toggleItalicMark(editor) {
    const isActive = CustomEditor.isItalicMarkActive(editor)
    if (isActive) {
      Editor.removeMark(editor, 'italic')
    } else {
      Editor.addMark(editor, 'italic', true)
    }
  },

  isBlockActive(editor, format) {
    const { selection } = editor
    if (!selection) return false

    const [match] = Array.from(
      Editor.nodes(editor, {
        at: Editor.unhangRange(editor, selection),
        match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === format,
      })
    )

    return !!match
  },

  toggleBlock(editor, format) {
    const isActive = CustomEditor.isBlockActive(editor, format)
    const isList = ['numbered-list', 'bulleted-list', 'inline-verbatim-container'].includes(format)

    // First unwrap any existing lists
    Transforms.unwrapNodes(editor, {
      match: (n) =>
        !Editor.isEditor(n) &&
        SlateElement.isElement(n) &&
        ['numbered-list', 'bulleted-list', 'ul', 'ol', 'inline-verbatim-container'].includes(
          n.type
        ),
      split: true,
    })

    // If we're turning off a list or turning on a non-list block, convert list items to paragraphs
    if (isActive || !isList) {
      Transforms.setNodes(editor, {
        type: 'paragraph',
      })
    }

    // If we're not turning off the block type and it's not a list, set the new block type
    if (!isActive && !isList) {
      Transforms.setNodes(editor, {
        type: format,
      })
    }

    // If we're turning on a list and it's not already active, wrap in the list type
    if (!isActive && isList) {
      Transforms.setNodes(editor, {
        type: 'list-item',
      })
      Transforms.wrapNodes(editor, {
        type: format,
        children: [],
      })
    }
  },

  toggleInlineVerbatim(editor) {
    const isActive = CustomEditor.isBlockActive(editor, 'inline-verbatim')

    const listTypes = ['bulleted-list', 'numbered-list', 'inline-verbatim-container']
    Transforms.unwrapNodes(editor, {
      match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) && listTypes.includes(n.type),
      split: true,
    })

    Transforms.setNodes(editor, {
      type: isActive ? 'paragraph' : 'inline-verbatim',
    })

    if (!isActive) {
      const block = { type: 'inline-verbatim-container', children: [] }
      Transforms.wrapNodes(editor, block)
    }
  },

  insertImage(editor, url) {
    const image = {
      type: 'image',
      url,
      children: [{ text: '' }],
      isVoid: true,
    }

    Transforms.splitNodes(editor, { mode: 'highest' })

    let pointAfter
    if (editor.selection) {
      const after = Editor.after(editor, editor.selection)
      pointAfter = after ? after.path : [editor.children.length]
    } else {
      pointAfter = [editor.children.length]
    }

    Transforms.insertNodes(editor, image, { at: [pointAfter[0]] })
    // Insert a paragraph after the image to allow continued editing
    Transforms.insertNodes(
      editor,
      { type: 'paragraph', children: [{ text: '' }] },
      { at: [pointAfter[0] + 1] }
    )
  },

  insertHorizontalRule(editor) {
    const hr = { type: 'horizontal-rule', isVoid: true, children: [{ text: '' }] }

    Transforms.splitNodes(editor, { mode: 'highest' })
    let pointAfter = Editor.after(editor, editor.selection)?.path

    if (isNil(pointAfter)) {
      // this only happens when selection is at the end of the document
      pointAfter = [editor.children.length]
      Transforms.insertNodes(editor, hr, { at: pointAfter })
    } else {
      Transforms.insertNodes(editor, hr, { at: [pointAfter[0]] })
    }
    // Insert a paragraph after the HR to allow continued editing
    Transforms.insertNodes(
      editor,
      { type: 'paragraph', children: [{ text: '' }] },
      { at: [pointAfter[0] + 1] }
    )
  },

  wrapLink(editor, url) {
    const { selection } = editor
    const isCollapsed = selection && Range.isCollapsed(selection)
    const link = {
      type: 'link',
      url,
      children: isCollapsed ? [{ text: url }] : [],
    }

    if (isCollapsed) {
      Transforms.insertNodes(editor, link)
    } else {
      Transforms.wrapNodes(editor, link, { split: true })
      Transforms.collapse(editor, { edge: 'end' })
    }
  },
}

export const withInlines = (editor) => {
  const { isInline, isVoid, insertText } = editor

  editor.isInline = (element) => ['link', 'button'].includes(element.type) || isInline(element)

  editor.isVoid = (element) => {
    return ['image', 'horizontal-rule', 'hr'].includes(element.type) || isVoid(element)
  }

  // Override insertText to handle links and buttons
  // secures that you can continue typing outside the inlines, if the inlined elem is the only child
  // there has to be a better way, but i couldn't figure it out
  editor.insertText = (text) => {
    const { selection } = editor
    const [inlineEntry] = Editor.nodes(editor, {
      match: (n) => !Editor.isEditor(n) && ['link', 'button', 'hr'].includes(n.type),
    })
    if (inlineEntry) {
      const [parent, parentPath] = Editor.node(editor, [...selection.focus.path.slice(0, -2)])
      if (parent.children.length === 1) {
        Transforms.insertNodes(editor, { text: '' }, { at: [...parentPath, 0] })
        Transforms.insertNodes(editor, { text: '' }, { at: [...parentPath, 2] })
      }
      const isStart = selection.anchor.offset === 0
      const isEnd = selection.anchor.offset === inlineEntry[0].children[0].text.length
      if (isStart) {
        Transforms.move(editor, { distance: 2, unit: 'offset', reverse: true })
      }
      if (isEnd) {
        Transforms.move(editor, { distance: 1, unit: 'offset' })
      }
    }

    insertText(text)
  }

  return editor
}

/* when adding a new rule, if you are looking to create a new tag, add it to the appropriate tag
however if you're looking to override behavior of an HTML tag we are using data-type to indicate
that and data-id so it can be parsed and added to the trigger dict in server */
// Configuration for converting HTML to Slate
export const deserializeConfig = {
  elementTags: {
    p: () => ({ type: 'paragraph' }),
    h1: () => ({ type: 'h1' }),
    h2: () => ({ type: 'h2' }),
    h3: () => ({ type: 'h3' }),
    ul: (el) => {
      const type = el.attribs['data-type']
      return type === 'inline-verbatim-container'
        ? { type: 'inline-verbatim-container' }
        : { type: 'bulleted-list' }
    },
    ol: () => ({ type: 'numbered-list' }),
    li: (el) => {
      const type = el.attribs['data-type']
      return type === 'inline-verbatim'
        ? {
            type: 'inline-verbatim',
            'data-id': el.attribs['data-id'],
          }
        : { type: 'list-item' }
    },
    hr: () => ({ type: 'horizontal-rule' }),
    img: (el) => ({ type: 'image', url: el.attribs.src }),
    a: (el) => ({ type: 'link', url: el.attribs.href }),
    button: (el) => ({ type: 'button', url: el.attribs.href }),
  },
  textTags: {
    strong: () => ({ bold: true }),
    b: () => ({ bold: true }),
    em: () => ({ italic: true }),
    i: () => ({ italic: true }),
  },
}

// Configuration for converting Slate to HTML
export const serializeConfig = {
  elementMap: {
    paragraph: 'p',
    h1: 'h1',
    h2: 'h2',
    h3: 'h3',
    'horizontal-rule': 'hr',
    'bulleted-list': 'ul',
    'numbered-list': 'ol',
    'list-item': 'li',
  },
  elementTransforms: {
    image: ({ node }) => {
      return new Element('img', {
        src: node.url,
      })
    },
    link: ({ node, children }) => {
      return new Element('a', { href: node.url, target: '_blank' }, children)
    },
    button: ({ node, children }) => {
      return new Element(
        'button',
        {
          class: 'button',
          type: 'button',
          href: node.url,
        },
        children
      )
    },
    'inline-verbatim-container': ({ children }) => {
      return new Element(
        'ul',
        {
          class: 'inline-verbatim-container',
          'data-type': 'inline-verbatim-container',
        },
        children
      )
    },
    'inline-verbatim': ({ children }) => {
      return new Element(
        'li',
        {
          class: 'inline-verbatim',
          'data-type': 'inline-verbatim',
          'data-id': v4(),
        },
        children
      )
    },
  },
  markMap: {
    bold: ['strong'],
    italic: ['em'],
  },
}
// this defines how you render nodes, a node is a "block", typically nodes won't coexist
// except for links/buttons which are "inline" nodes
export const renderNode = (props) => {
  switch (props.element.type) {
    case 'list-item':
      return <li {...props.attributes}>{props.children}</li>
    case 'bulleted-list':
      return <ul {...props.attributes}>{props.children}</ul>
    case 'numbered-list':
      return <ol {...props.attributes}>{props.children}</ol>
    case 'h1':
    case 'header-one':
      return <h1 {...props.attributes}>{props.children}</h1>
    case 'h2':
    case 'header-two':
      return <h2 {...props.attributes}>{props.children}</h2>
    case 'horizontal-rule':
      return (
        <div {...props.attributes} contentEditable={false} data-slate-node="element">
          <hr />
          {props.children}
        </div>
      )
    case 'button':
      return (
        <a {...props.attributes}>
          <Button primary onClick={(e) => e.preventDefault()} size="small">
            {props.children}
          </Button>
        </a>
      )
    case 'image':
      return (
        <div {...props.attributes}>
          <div contentEditable={false} style={{ position: 'relative' }}>
            <img
              src={props.element.url}
              alt=""
              width="425"
              style={{ display: 'block', cursor: 'move' }}
              draggable="true"
            />
          </div>
          {props.children}
        </div>
      )
    case 'link':
      return (
        <a
          {...props.attributes}
          // target blank is important or preview links will open on current page
          target="_blank"
          rel="noopener noreferrer"
          href={props.element.url}
        >
          {props.children}
        </a>
      )
    case 'inline-verbatim-container':
      return (
        <ul {...props.attributes} style={{ listStyleType: 'circle' }}>
          {props.children}
        </ul>
      )
    case 'inline-verbatim':
      return <li {...props.attributes}>{props.children}</li>
    default:
      return <p {...props.attributes}>{props.children}</p>
  }
}
